import { useQuery, useMutation, useQueryClient } from 'react-query'
import axios from 'axios'

// Hook to manage onboarding state
export const useOnboarding = () => {
  const queryClient = useQueryClient()

  // Get onboarding status
  const {
    data: onboardingData,
    isLoading,
    error,
    refetch
  } = useQuery(
    'onboarding-checklist',
    () => axios.get('/clients/onboarding/checklist').then(res => res.data),
    {
      refetchOnWindowFocus: false,
      staleTime: 1000 * 60 * 5, // 5 minutes
    }
  )

  // Update onboarding item
  const updateItemMutation = useMutation(
    ({ itemId, completed }) => 
      axios.post('/clients/onboarding/update', { itemId, completed }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('onboarding-checklist')
        queryClient.invalidateQueries('client-profile')
      }
    }
  )

  // Complete onboarding
  const completeOnboardingMutation = useMutation(
    () => axios.post('/clients/onboarding/complete'),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('onboarding-checklist')
        queryClient.invalidateQueries('client-profile')
      }
    }
  )

  // Check if modal should be shown
  const shouldShowModal = () => {
    if (!onboardingData || isLoading) return false
    return !onboardingData.onboardingCompleted
  }

  // Check if this is first login
  const isFirstLogin = () => {
    if (!onboardingData || isLoading) return false
    return !onboardingData.firstLoginCompleted
  }

  // Get completion percentage
  const getCompletionPercentage = () => {
    if (!onboardingData) return 0
    return Math.round((onboardingData.completedCount / onboardingData.totalCount) * 100)
  }

  // Get required steps completion
  const getRequiredStepsCompletion = () => {
    if (!onboardingData) return { completed: 0, total: 0 }
    const requiredSteps = onboardingData.checklist.filter(item => item.required)
    const completedRequired = requiredSteps.filter(item => item.completed).length
    return {
      completed: completedRequired,
      total: requiredSteps.length,
      isComplete: completedRequired === requiredSteps.length
    }
  }

  return {
    onboardingData,
    isLoading,
    error,
    refetch,
    shouldShowModal,
    isFirstLogin,
    getCompletionPercentage,
    getRequiredStepsCompletion,
    updateItem: updateItemMutation.mutate,
    completeOnboarding: completeOnboardingMutation.mutate,
    isUpdatingItem: updateItemMutation.isLoading,
    isCompletingOnboarding: completeOnboardingMutation.isLoading
  }
}

// Onboarding storage helpers
export const onboardingStorage = {
  // Mark that user has seen the modal (to avoid showing it multiple times in same session)
  markModalSeen: () => {
    sessionStorage.setItem('onboarding_modal_seen', 'true')
  },
  
  // Check if modal was seen in this session
  wasModalSeen: () => {
    return sessionStorage.getItem('onboarding_modal_seen') === 'true'
  },
  
  // Clear modal seen flag (for testing)
  clearModalSeen: () => {
    sessionStorage.removeItem('onboarding_modal_seen')
  },

  // Store onboarding preference
  setSkippedForSession: (skipped = true) => {
    sessionStorage.setItem('onboarding_skipped', skipped.toString())
  },

  // Check if user skipped onboarding for this session
  isSkippedForSession: () => {
    return sessionStorage.getItem('onboarding_skipped') === 'true'
  }
}

export default useOnboarding